import React, { useEffect } from "react";
import styles from "./btn.module.scss";

interface ButtonProps {
  children?: React.ReactNode;
  type?: "button" | "submit" | "reset";
  size?: "sm" | "md" | "lg" | "pill" | "select_fare" | "flight_cost" | "apply_btn" | "refresh_btn" | "full_width";
  variant?:
  | "default"
  | "cancel"
  | "success"
  | "newDefault"
  | "text"
  | "secondary"
  | "transparent"
  | "tertiary"
  | "fullyTransparent"
  | "newTertiary"
  | "custom";
  disabled?: boolean;
  /**
   * Shows an inline spinner and keeps the button visually "active" (un-dims the
   * disabled state) while an async prerequisite is loading. Non-icon mode only.
   * Pair with `disabled` so the button stays non-clickable while loading.
   */
  loading?: boolean;
  onClick?: (event: any) => void;
  className?: string;
  style?: any;
  onMouseEnter?: any;
  onMouseLeave?: any;
  // Icon-mode props (from BtnWithTextAndIcon)
  btnClassName?: string;
  btnText?: string;
  textInfo?: string;
  preIcon?: any;
  preSvgIcon?: any;
  postIcon?: any;
  iconBtn?: string;
  svgIcon?: any;
  iconHeight?: number;
  translateX?: number;
  translateY?: number;
  color?: any;
  preIconPaddingRight?: number;
}

const AppButton: React.FC<ButtonProps> = ({
  children,
  type = "button",
  size = "md",
  disabled = false,
  loading = false,
  onClick,
  className,
  variant = "default",
  style = {},
  onMouseEnter = () => { },
  onMouseLeave = () => { },
  // Icon-mode props
  btnClassName = "",
  btnText,
  textInfo = "",
  preIcon = "",
  preSvgIcon = "",
  postIcon = "",
  iconBtn = "",
  svgIcon = "",
  iconHeight = 17,
  translateX = 0,
  translateY = 0,
  color = "",
  preIconPaddingRight = 10,
}) => {
  const isIconMode = btnText !== undefined || iconBtn || preIcon || preSvgIcon || postIcon || svgIcon || textInfo;

  useEffect(() => {
    if (!isIconMode) return;
    const handleTouchStart = () => {};
    document.addEventListener("touchstart", handleTouchStart, true);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart, true);
    };
  }, [isIconMode]);

  if (isIconMode) {
    const btnComputedClassName = `${btnClassName} ${styles.continueBtn} ${preIcon !== "" && styles.preIcon} ${preSvgIcon !== "" && styles.preIcon} ${iconBtn !== "" && styles.iconBtn} ${svgIcon !== "" && styles.iconBtn}`;
    const buttonClassName = `${btnComputedClassName} ${styles.btn} ${styles[`btn-${size}`]} ${styles[`btn-${variant}`]}`;
    return (
      <div className={`${className || ""} ${styles.continueBtnContainer}`}>
        <button
          type={type}
          disabled={disabled}
          onClick={onClick}
          className={buttonClassName}
          style={onMouseEnter ? { color: color } : {}}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {svgIcon !== "" && (
            <div style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}>
              {svgIcon}
            </div>
          )}
          {iconBtn !== "" && (
            <img
              src={iconBtn}
              alt="back"
              style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}
            />
          )}
          {preSvgIcon !== "" && (
            <div style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}>
              {preSvgIcon}
            </div>
          )}
          {preIcon !== "" && (
            <img
              src={preIcon}
              alt="back"
              style={{ height: `${iconHeight}px`, paddingRight: `${preIconPaddingRight}px` }}
            />
          )}
          {btnText}
          {postIcon !== "" && <img src={postIcon} alt="forward" style={{ height: `${iconHeight}px` }} />}
        </button>
        {textInfo && <p>{textInfo}</p>}
      </div>
    );
  }

  const computedClassName = `${className} ${styles.btn} ${styles[`btn-${size}`]} ${styles[`btn-${variant}`]} ${loading ? styles.btnLoading : ""}`;
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={computedClassName}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {loading && <span className={styles.btnSpinner} aria-hidden="true" />}
      {children}
    </button>
  );
};

export default AppButton;
